import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import { trans, iconColors } from '../../utils';
/**
 * Tag picker for the inline composer.
 *
 * Renders the trigger (chips for selected tags + placeholder) and the
 * dropdown (filterable list, hierarchical primary/child tags, optional
 * "bypass minimums" checkbox for privileged users).
 *
 * All state lives on `InlineComposerState`; this component is a thin view.
 */
export default class TagPicker extends Component {
  view() {
    const state = this.attrs.state;
    const limits = this.readLimits();
    const {
      primaryCount,
      secondaryCount
    } = this.countSelected(state);
    const allTags = app.store.all('tags').filter(Boolean);
    const rootTags = allTags.filter(t => !t.isChild?.()).sort((a, b) => (a.position?.() ?? 9999) - (b.position?.() ?? 9999));

    // Build a flat list interleaving root tags with their children.
    const tagItems = [];
    for (const root of rootTags) {
      tagItems.push({
        tag: root,
        isChild: false
      });
      allTags.filter(t => t.isChild?.() && t.parent?.()?.id?.() === root.id?.()).sort((a, b) => (a.position?.() ?? 9999) - (b.position?.() ?? 9999)).forEach(child => tagItems.push({
        tag: child,
        isChild: true
      }));
    }
    const visible = limits.maxSecondary === 0 && !state.tagBypassReqs ? tagItems.filter(_ref => {
      let {
        isChild
      } = _ref;
      return !isChild;
    }) : tagItems;
    const filter = (state.tagFilter || '').toLowerCase();
    const filtered = filter ? visible.filter(_ref2 => {
      let {
        tag
      } = _ref2;
      return tag.name?.().toLowerCase().includes(filter);
    }) : visible;
    const instruction = this.computeInstruction(state, limits, primaryCount, secondaryCount);
    return m("div", {
      className: "AvocadoHome-tagPicker"
    }, this.renderTrigger(state, instruction), state.tagPickerOpen && this.renderDropdown(state, filtered, limits, primaryCount, secondaryCount));
  }

  // ── Subviews ───────────────────────────────────────────────────────────

  renderTrigger(state, instruction) {
    return m("button", {
      className: `AvocadoHome-tagPickerTrigger${state.tagPickerOpen ? ' is-open' : ''}`,
      type: "button",
      onclick: e => {
        e.preventDefault();
        e.stopPropagation();
        state.tagPickerOpen = !state.tagPickerOpen;
        if (!state.tagPickerOpen) state.tagFilter = '';
        m.redraw();
      }
    }, m("i", {
      className: "fas fa-tag",
      "aria-hidden": "true"
    }), state.tags.length === 0 && m("span", {
      className: "AvocadoHome-tagPickerPlaceholder"
    }, instruction || trans('ramon-avocado.forum.home.choose_tags', 'Choose tags')), state.tags.map(tag => {
      const tagColor = tag.color?.() || null;
      return m("span", {
        key: tag.id?.(),
        className: "AvocadoHome-tagChip",
        style: tagColor ? {
          '--tag-color': iconColors(tagColor).color
        } : {},
        onclick: e => {
          e.preventDefault();
          e.stopPropagation();
          state.removeTag(tag);
          m.redraw();
        },
        title: "Remove tag"
      }, tag.icon?.() && m("i", {
        className: tag.icon(),
        "aria-hidden": "true"
      }), tag.name?.(), m("i", {
        className: "fas fa-times AvocadoHome-tagChipRemoveIcon",
        "aria-hidden": "true"
      }));
    }), m("i", {
      className: `fas fa-chevron-${state.tagPickerOpen ? 'up' : 'down'} AvocadoHome-tagPickerChevron`,
      "aria-hidden": "true"
    }));
  }
  renderDropdown(state, items, limits, primaryCount, secondaryCount) {
    return m("div", {
      className: "AvocadoHome-tagPickerDropdown"
    }, m("div", {
      className: "AvocadoHome-tagPickerSearch"
    }, m("i", {
      className: "fas fa-search",
      "aria-hidden": "true"
    }), m("input", {
      type: "text",
      placeholder: trans('ramon-avocado.forum.home.filter_tags', 'Filter tags'),
      value: state.tagFilter || '',
      oninput: e => {
        state.tagFilter = e.target.value;
        m.redraw();
      },
      onclick: e => e.stopPropagation(),
      oncreate: vnode => setTimeout(() => vnode.dom.focus(), 0)
    })), items.length === 0 ? m("span", {
      className: "AvocadoHome-tagPickerEmpty"
    }, trans('ramon-avocado.forum.home.no_tags_found', 'No tags found')) : m("ul", {
      className: "AvocadoHome-tagPickerList"
    }, items.map(_ref3 => {
      let {
        tag,
        isChild
      } = _ref3;
      return this.renderItem(state, tag, isChild, limits, primaryCount, secondaryCount);
    })), limits.canBypass && m("label", {
      className: "AvocadoHome-tagPickerBypass",
      onclick: e => e.stopPropagation()
    }, m("input", {
      type: "checkbox",
      checked: state.tagBypassReqs,
      onchange: e => {
        state.tagBypassReqs = e.target.checked;
        m.redraw();
      }
    }), ' ' + trans('ramon-avocado.forum.home.bypass_tag_requirements', 'Bypass tag requirements')));
  }
  renderItem(state, tag, isChild, limits, primaryCount, secondaryCount) {
    const tagId = tag.id?.();
    const isSelected = state.tags.includes(tag);
    // Tags sem cor customizada caem no `--primary-color` da skin via CSS;
    // setar `background: ''` inline anula a regra LESS e deixa o ícone
    // (color:#fff) invisível sobre fundo transparente no tema claro.
    const tagColor = tag.color?.() || null;
    const selectable = this.canSelectTag(tag, state, limits, primaryCount, secondaryCount);
    const className = ['AvocadoHome-tagPickerItem', isChild && 'is-child', isSelected && 'is-selected', !selectable && !isSelected && 'is-disabled'].filter(Boolean).join(' ');
    return m("li", {
      key: tagId,
      className: className,
      onclick: e => {
        e.preventDefault();
        e.stopPropagation();
        if (!selectable && !isSelected) return;
        if (isSelected) state.removeTag(tag);else state.addTag(tag);
        m.redraw();
      }
    }, m("span", {
      className: "AvocadoHome-tagPickerItem-icon",
      style: tagColor ? {
        background: tagColor
      } : {}
    }, m("i", {
      className: tag.icon?.() || 'fas fa-tag',
      "aria-hidden": "true"
    })), m("span", {
      className: "AvocadoHome-tagPickerItem-name"
    }, tag.name?.()), tag.description?.() && m("span", {
      className: "AvocadoHome-tagPickerItem-desc"
    }, tag.description()), isSelected && m("i", {
      className: "fas fa-check AvocadoHome-tagPickerItem-check",
      "aria-hidden": "true"
    }));
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  readLimits() {
    const rawMaxP = parseInt(app.forum.attribute('maxPrimaryTags'));
    const rawMaxS = parseInt(app.forum.attribute('maxSecondaryTags'));
    return {
      maxPrimary: isNaN(rawMaxP) ? Infinity : rawMaxP,
      maxSecondary: isNaN(rawMaxS) ? Infinity : rawMaxS,
      minPrimary: parseInt(app.forum.attribute('minPrimaryTags')) || 0,
      minSecondary: parseInt(app.forum.attribute('minSecondaryTags')) || 0,
      canBypass: !!app.forum.attribute('canBypassTagCounts')
    };
  }
  countSelected(state) {
    return {
      primaryCount: state.tags.filter(t => t.position?.() !== null && !t.isChild?.()).length,
      secondaryCount: state.tags.filter(t => t.position?.() === null).length
    };
  }
  canSelectTag(tag, state, limits, primaryCount, secondaryCount) {
    if (state.tagBypassReqs || state.tags.includes(tag)) return true;
    const isPrimary = tag.position?.() !== null && !tag.isChild?.();
    if (!isPrimary && primaryCount === 0) return false;
    if (isPrimary && primaryCount >= limits.maxPrimary) return false;
    if (!isPrimary && secondaryCount >= limits.maxSecondary) return false;
    return true;
  }
  computeInstruction(state, limits, primaryCount, secondaryCount) {
    if (state.tagBypassReqs) return '';
    if (primaryCount < limits.minPrimary) {
      const n = limits.minPrimary - primaryCount;
      return n === 1 ? trans('ramon-avocado.forum.home.choose_primary_tag_singular', 'Choose 1 primary tag') : trans('ramon-avocado.forum.home.choose_primary_tag_plural', `Choose ${n} primary tags`, {
        count: n
      });
    }
    if (secondaryCount < limits.minSecondary) {
      const n = limits.minSecondary - secondaryCount;
      return n === 1 ? trans('ramon-avocado.forum.home.choose_secondary_tag_singular', 'Choose 1 secondary tag') : trans('ramon-avocado.forum.home.choose_secondary_tag_plural', `Choose ${n} secondary tags`, {
        count: n
      });
    }
    return '';
  }
}
flarum.reg.add('ramon-avocado', 'forum/components/shared/TagPicker', TagPicker);